import { DateRangeID } from '@fullstory/client-api';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import { TimeRangeComparison } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { DateRangeOption } from '../../types/dateRange';
import { intl } from '@fullstory/intl';
import { dateFormat, isMidnight } from '@fullstory/util/date';
import moment from 'moment';

export const dateRanges: DateRangeOption[] = [
  {
    id: DateRangeID.Past60Minutes,
    label: intl.formatMessage({ defaultMessage: 'Past 60 minutes', id: 'dycj1L' }),
    days: 1.0 / 24.0,
  },
  {
    id: DateRangeID.Past3Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 3 hours', id: 'f9/17e' }),
    days: 3.0 / 24.0,
  },
  {
    id: DateRangeID.Past6Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 6 hours', id: '/WVb/X' }),
    days: 6.0 / 24.0,
  },
  {
    id: DateRangeID.Past24Hours,
    label: intl.formatMessage({ defaultMessage: 'Past 24 hours', id: '/a7cg5' }),
    days: 1,
  },
  {
    id: DateRangeID.Today,
    label: intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGg' }),
    days: 1,
  },
  {
    id: DateRangeID.Yesterday,
    label: intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP' }),
    days: 1,
  },
  {
    id: DateRangeID.PastWeek,
    label: intl.formatMessage({ defaultMessage: 'Past 7 days', id: 'mbbNJS' }),
    days: 7,
  },
  {
    id: DateRangeID.PastMonth,
    label: intl.formatMessage({ defaultMessage: 'Past 30 days', id: 'OIM20v' }),
    days: 30,
  },
  {
    id: DateRangeID.Past90Days,
    label: intl.formatMessage({ defaultMessage: 'Past 90 days', id: 'ZOWtIg' }),
    days: 90,
  },
  {
    id: DateRangeID.Since,
    label: intl.formatMessage({ defaultMessage: 'Since', id: '+eH89J' }),
    days: 0,
  },
  {
    id: DateRangeID.Custom,
    label: intl.formatMessage({ defaultMessage: 'Custom', id: 'Sjo1P4' }),
    days: 0,
  },
];

export interface IntervalOption {
  id: TimeInterval;
  label: string;
}

const TIME_INTERVAL_MINUTE_OPTION = {
  id: TimeInterval.TIME_INTERVAL_MINUTE,
  label: intl.formatMessage({ defaultMessage: 'Every Minute', id: 'FoVRBo' }),
};
const TIME_INTERVAL_HOUR_OPTION = {
  id: TimeInterval.TIME_INTERVAL_HOUR,
  label: intl.formatMessage({ defaultMessage: 'Hourly', id: 'Kj+Lfx' }),
};
const TIME_INTERVAL_DAY_OPTION = {
  id: TimeInterval.TIME_INTERVAL_DAY,
  label: intl.formatMessage({ defaultMessage: 'Daily', id: 'zxvhnE' }),
};
const TIME_INTERVAL_WEEK_OPTION = {
  id: TimeInterval.TIME_INTERVAL_WEEK,
  label: intl.formatMessage({ defaultMessage: 'Weekly', id: '/clOBU' }),
};
const TIME_INTERVAL_MONTH_OPTION = {
  id: TimeInterval.TIME_INTERVAL_MONTH,
  label: intl.formatMessage({ defaultMessage: 'Monthly', id: 'wYsv4Z' }),
};
const TIME_INTERVAL_ENTIRE_RANGE_OPTION = {
  id: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
  label: intl.formatMessage({ defaultMessage: 'Entire Range', id: 'OiBFIX' }),
};

export const timeIntervalOptions: Partial<{ [key in TimeInterval]: IntervalOption }> = {
  [TimeInterval.TIME_INTERVAL_MINUTE]: TIME_INTERVAL_MINUTE_OPTION,
  [TimeInterval.TIME_INTERVAL_HOUR]: TIME_INTERVAL_HOUR_OPTION,
  [TimeInterval.TIME_INTERVAL_DAY]: TIME_INTERVAL_DAY_OPTION,
  [TimeInterval.TIME_INTERVAL_WEEK]: TIME_INTERVAL_WEEK_OPTION,
  [TimeInterval.TIME_INTERVAL_MONTH]: TIME_INTERVAL_MONTH_OPTION,
  [TimeInterval.TIME_INTERVAL_ENTIRE_RANGE]: TIME_INTERVAL_ENTIRE_RANGE_OPTION,
};

export const getIntervals = (opts: {
  includeMinute: boolean;
  includeEntireRange: boolean;
}): IntervalOption[] => {
  const intervals = [
    TIME_INTERVAL_HOUR_OPTION,
    TIME_INTERVAL_DAY_OPTION,
    TIME_INTERVAL_WEEK_OPTION,
    TIME_INTERVAL_MONTH_OPTION,
  ];
  if (opts.includeMinute) {
    intervals.unshift(TIME_INTERVAL_MINUTE_OPTION);
  }
  if (opts.includeEntireRange) {
    intervals.push(TIME_INTERVAL_ENTIRE_RANGE_OPTION);
  }
  return intervals;
};

// TODO: @juliarapoport add type ({[id in TimeRangeComparison.ComparisonDateRangeID]: string})
// when TS has been updated to 4.4 and keyofStringsOnly can be set to false in legacy-app
export const comparisonLabels = {
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD]: intl.formatMessage({
    defaultMessage: 'Previous period',
    id: 'hCCWuZ',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_DAY]: intl.formatMessage({
    defaultMessage: 'Previous day',
    id: 'RMO5xM',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_WEEK]: intl.formatMessage({
    defaultMessage: 'Previous week',
    id: 'GAjQnO',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_MONTH]: intl.formatMessage({
    defaultMessage: 'Previous month',
    id: '8mRNBD',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_QUARTER]: intl.formatMessage({
    defaultMessage: 'Previous quarter',
    id: 'hQS3l9',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_YEAR]: intl.formatMessage({
    defaultMessage: 'Previous year',
    id: 'smN0U9',
  }),
};

export const changeFromComparisonLabels = {
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD]: intl.formatMessage({
    defaultMessage: 'Change from previous period:',
    id: 'LFdTuS',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_DAY]: intl.formatMessage({
    defaultMessage: 'Change from previous day:',
    id: 'USnGrF',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_WEEK]: intl.formatMessage({
    defaultMessage: 'Change from previous week:',
    id: 'BuFMoG',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_MONTH]: intl.formatMessage({
    defaultMessage: 'Change from previous month:',
    id: 'sibvyX',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_QUARTER]: intl.formatMessage({
    defaultMessage: 'Change from previous quarter:',
    id: '94pj6p',
  }),
  [TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_YEAR]: intl.formatMessage({
    defaultMessage: 'Change from previous year:',
    id: 'tus7L1',
  }),
};

const CUSTOM = 'CUSTOM' as 'CUSTOM';
export const comparisons = [
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD],
  },
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_DAY,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_DAY],
  },
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_WEEK,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_WEEK],
  },
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_MONTH,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_MONTH],
  },
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_QUARTER,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_QUARTER],
  },
  {
    id: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_YEAR,
    label: comparisonLabels[TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_YEAR],
  },
  { id: CUSTOM, label: intl.formatMessage({ defaultMessage: 'Custom', id: 'Sjo1P4' }) },
];

export type ComparisonID = (typeof comparisons)[0]['id'];
export function isCannedID(id: ComparisonID): id is TimeRangeComparison.ComparisonDateRangeID {
  return id !== CUSTOM;
}

export const getFormattedDate = (date: Date): string => {
  const cloned = new Date(date.getTime());
  // If the date is at midnight, it actually represents the previous day
  if (cloned.getHours() === 0 && cloned.getMinutes() === 0 && cloned.getSeconds() === 0) {
    cloned.setDate(cloned.getDate() - 1);
  }
  return dateFormat(cloned, { format: 'abbreviated' });
};

export const comparisonSegmentLimitMsg = intl.formatMessage({
  defaultMessage:
    'A limit of 1 segment can be selected if you wish to view a comparison time range.',
  id: 'CigiyO',
});

export const shouldIncludeTimeInputs = (
  dateRangeId: DateRangeID | null,
  interval: TimeInterval | null,
  disabled: boolean,
  rangeEnd: Date | null,
) => {
  let includeTimeInputs = false;

  if (
    interval === TimeInterval.TIME_INTERVAL_MINUTE ||
    interval === TimeInterval.TIME_INTERVAL_HOUR ||
    interval === TimeInterval.TIME_INTERVAL_ENTIRE_RANGE
  ) {
    includeTimeInputs = true;
  }

  if (
    dateRangeId === DateRangeID.Today ||
    dateRangeId === DateRangeID.Yesterday ||
    dateRangeId === DateRangeID.Past60Minutes ||
    dateRangeId === DateRangeID.Past3Hours ||
    dateRangeId === DateRangeID.Past6Hours ||
    dateRangeId === DateRangeID.Past24Hours
  ) {
    includeTimeInputs = true;
  }

  if (disabled) {
    // no flag, no go
    includeTimeInputs = false;
  }

  // If we're in a custom range and a specific time isn't selected
  // we don't want to show the time inputs
  if (
    dateRangeId === DateRangeID.Custom &&
    (!rangeEnd || (rangeEnd && isMidnight(moment(rangeEnd))))
  ) {
    includeTimeInputs = false;
  }

  return includeTimeInputs;
};
